import type { ChatSummary } from '../api/client.ts';

/**
 * The order the chat list is drawn in: pinned people, the rest of the people, a
 * divider, then bots.
 *
 * Here rather than in ChatList.tsx because none of it is a rendering question —
 * it is sorting, grouping and one plural — and because the rules that matter
 * are the ones easiest to break silently: a bot that jumps above the divider
 * when it writes, a divider drawn over nothing, a folder that sorts by when it
 * was made instead of by what is newest inside it, a pinned row that drifts
 * down as soon as somebody else writes.
 *
 * There is no filter, no tab and no «Боты» screen, which is the design's
 * decision rather than an omission: with two halves and a label between them
 * there is nothing to choose and nowhere to come back from.
 */

export type ChatListEntry =
  | { kind: 'chat'; chat: ChatSummary }
  | { kind: 'divider' }
  | { kind: 'folder'; name: string; chats: ChatSummary[] };

function timeOf(chat: ChatSummary): number {
  return chat.lastMessage?.createdAt ?? 0;
}

function newestFirst(left: ChatSummary, right: ChatSummary): number {
  return timeOf(right) - timeOf(left);
}

/**
 * The order the people half is drawn in: what somebody pinned, newest pin at the
 * top, then everybody else by their newest message.
 *
 * Pins sort by when they were made rather than by what is newest inside them,
 * and that is the point of pinning: a row that still moved when somebody wrote
 * to it would be exactly the list one was trying to escape. A message arriving
 * in a chat below still lifts it — to just under the pins, and no further.
 *
 * Only the people half. A bot cannot be pinned (canPin on the server says so),
 * and this comparator is never asked about one — but it would be harmless if it
 * were, because the divider is applied after the sort and is not a key any of
 * this can beat.
 */
export function pinnedFirst(left: ChatSummary, right: ChatSummary): number {
  if (left.pinnedAt !== right.pinnedAt) return (right.pinnedAt ?? 0) - (left.pinnedAt ?? 0);
  return newestFirst(left, right);
}

/** When a folder last had anything in it — what its row shows and sorts by. */
export function folderTime(chats: ChatSummary[]): number {
  return chats.reduce((newest, chat) => Math.max(newest, timeOf(chat)), 0);
}

/** One badge for the whole folder: what is unread across every bot inside. */
export function folderUnread(chats: ChatSummary[]): number {
  return chats.reduce((total, chat) => total + chat.unreadCount, 0);
}

/**
 * The bot whose message the folder's row shows.
 *
 * The row says «Заявки: новая заявка» rather than the message alone: a folder
 * holds several bots, and a snippet with no name in front of it is a sentence
 * with no idea who said it.
 */
export function folderLead(chats: ChatSummary[]): ChatSummary | null {
  return [...chats].sort(newestFirst)[0] ?? null;
}

/**
 * People, the divider, then bots — folders and lone bots together, by time.
 *
 * A bot's new message moves it inside its half and no further: the divider is
 * not a sort key that a timestamp can beat, which is the whole point of having
 * one. Somebody with no bots gets exactly the list they had before any of this
 * existed — no divider, no empty section.
 */
export function buildChatList(chats: ChatSummary[]): ChatListEntry[] {
  const people: ChatSummary[] = [];
  const lone: ChatSummary[] = [];
  const folders = new Map<string, ChatSummary[]>();

  for (const chat of chats) {
    if (chat.bot === null) {
      people.push(chat);
      continue;
    }
    const group = chat.bot.group;
    if (group === null) {
      lone.push(chat);
      continue;
    }
    const held = folders.get(group);
    if (held) held.push(chat);
    else folders.set(group, [chat]);
  }

  const entries: ChatListEntry[] = people
    .sort(pinnedFirst)
    .map((chat): ChatListEntry => ({ kind: 'chat', chat }));

  if (lone.length === 0 && folders.size === 0) return entries;

  const bots: { at: number; entry: ChatListEntry }[] = [
    ...lone.map((chat) => ({ at: timeOf(chat), entry: { kind: 'chat', chat } as ChatListEntry })),
    ...[...folders].map(([name, held]) => ({
      at: folderTime(held),
      entry: { kind: 'folder', name, chats: [...held].sort(newestFirst) } as ChatListEntry,
    })),
  ];

  entries.push({ kind: 'divider' });
  for (const bot of bots.sort((left, right) => right.at - left.at)) entries.push(bot.entry);
  return entries;
}
